'use client';
import React, { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { PRICING } from '@/lib/mockData';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const CART_OPTIONS = [
  { value: 'none', label: 'No Cart', sub: 'Walk the course', price: 0 },
  { value: 'single', label: 'Single Cart', sub: '1 person per cart', price: PRICING.cartSingle },
  { value: 'double', label: 'Double Cart', sub: '2 persons per cart', price: PRICING.cartDouble },
] as const;

export default function BookingDetails({ booking, updateBooking, onNext, onBack }: Props) {
  const greenFeePerPlayer = booking.holes === 18 ? PRICING.greenFee18Visitor : PRICING.greenFee9Visitor;

  useEffect(() => {
    const greenFees = greenFeePerPlayer * booking.players;
    const cartFees = booking.cart === 'single' ? PRICING.cartSingle : booking.cart === 'double' ? PRICING.cartDouble : 0;
    updateBooking({ greenFees, cartFees, total: greenFees + cartFees });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.holes, booking.players, booking.cart]);

  const handlePlayerNameChange = (idx: number, value: string) => {
    const names = [...(booking.playerNames || [])];
    names[idx] = value;
    updateBooking({ playerNames: names });
  };

  const displayDate = booking.selectedDate
    ? new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-6">
        {/* Round Type */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <h2 className="text-lg font-bold text-foreground mb-5">Round Details</h2>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-3">Number of Holes</label>
            <div className="grid grid-cols-2 gap-3">
              {([18, 9] as const).map((h) => (
                <button
                  key={`holes-${h}`}
                  onClick={() => updateBooking({ holes: h })}
                  className={`p-4 rounded-xl border-2 text-left transition-all duration-150 ${
                    booking.holes === h
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 bg-card'
                  }`}
                >
                  <div className="text-lg font-bold text-foreground">{h} Holes</div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    R{h === 18 ? PRICING.greenFee18Visitor : PRICING.greenFee9Visitor} per player
                  </div>
                  {h === 18 && <div className="text-xs text-primary font-medium mt-1">Full round — Par 72</div>}
                  {h === 9 && <div className="text-xs text-muted-foreground mt-1">Front 9 — 2 885m</div>}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-foreground mb-3">Number of Players</label>
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((p) => (
                <button
                  key={`players-${p}`}
                  onClick={() => updateBooking({ players: p })}
                  className={`py-3 rounded-xl border-2 font-bold text-lg transition-all duration-150 ${
                    booking.players === p
                      ? 'border-primary bg-primary text-white' :'border-border hover:border-primary/40 text-foreground bg-card'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-3">Golf Cart</label>
            <div className="space-y-2.5">
              {CART_OPTIONS.map((opt) => (
                <button
                  key={`cart-${opt.value}`}
                  onClick={() => updateBooking({ cart: opt.value })}
                  className={`w-full p-4 rounded-xl border-2 text-left flex items-center justify-between transition-all duration-150 ${
                    booking.cart === opt.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 bg-card'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      booking.cart === opt.value ? 'border-primary' : 'border-border'
                    }`}>
                      {booking.cart === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-foreground">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.sub}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-foreground text-sm font-tabular">
                      {opt.price === 0 ? 'Free' : `R${opt.price}`}
                    </div>
                    {opt.price > 0 && <div className="text-xs text-muted-foreground">per round</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Player Names */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <h3 className="text-base font-bold text-foreground mb-4">Player Names</h3>
          <div className="space-y-3">
            {Array.from({ length: booking.players }, (_, i) => (
              <div key={`player-name-${i}`}>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Player {i + 1} {i === 0 && <span className="text-xs text-muted-foreground">(Primary)</span>}
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder={`Enter player ${i + 1} full name`}
                  value={booking.playerNames[i] || ''}
                  onChange={(e) => handlePlayerNameChange(i, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border-2 border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors flex items-center justify-center gap-2">
            <Icon name="ChevronLeftIcon" size={16} variant="outline" />
            Back
          </button>
          <button onClick={onNext} className="flex-1 btn-primary py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
            Continue to Registration
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </button>
        </div>
      </div>

      {/* Pricing Summary */}
      <div className="bg-card border border-border rounded-2xl p-5 shadow-card h-fit sticky top-20">
        <h3 className="font-bold text-foreground text-base mb-4 flex items-center gap-2">
          <Icon name="ReceiptPercentIcon" size={18} variant="outline" className="text-primary" />
          Booking Summary
        </h3>
        <div className="space-y-1 mb-4 pb-4 border-b border-border text-sm text-muted-foreground">
          <div className="flex justify-between"><span>Date</span><span className="text-foreground font-medium">{displayDate}</span></div>
          <div className="flex justify-between"><span>Tee Time</span><span className="text-foreground font-medium font-tabular">{booking.selectedTime}</span></div>
          <div className="flex justify-between"><span>Holes</span><span className="text-foreground font-medium">{booking.holes}</span></div>
          <div className="flex justify-between"><span>Players</span><span className="text-foreground font-medium">{booking.players}</span></div>
        </div>
        <div className="space-y-2 mb-4 pb-4 border-b border-border">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Green Fees ({booking.players} × R{greenFeePerPlayer})</span>
            <span className="font-semibold font-tabular">R{booking.greenFees}</span>
          </div>
          {booking.cartFees > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cart Hire</span>
              <span className="font-semibold font-tabular">R{booking.cartFees}</span>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center">
          <span className="font-bold text-foreground">Total</span>
          <span className="text-2xl font-extrabold text-primary font-tabular">R{booking.total}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-3">All prices in South African Rand (ZAR). Payment processed securely.</p>
      </div>
    </div>
  );
}